import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { t } from 'i18next';
import { Box, Slide, Snackbar, useTheme } from '@mui/material';
import {
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  WarningAmberOutlined,
  InfoOutlined,
  CloseOutlined,
} from '@mui/icons-material';

const SEVERITY_ICONS = {
  success: CheckCircleOutlined,
  error: ErrorOutlineOutlined,
  warning: WarningAmberOutlined,
  info: InfoOutlined,
};

const injectToastStyles = () => {
  if (typeof document === 'undefined' || document.getElementById('dn-toast-styles')) return;
  const el = document.createElement('style');
  el.id = 'dn-toast-styles';
  el.textContent = `
    @keyframes dn-toast-progress {
      from { transform: scaleX(1); }
      to   { transform: scaleX(0); }
    }
    .dn-toast-progress {
      transform-origin: left;
      animation-name: dn-toast-progress;
      animation-timing-function: linear;
      animation-fill-mode: both;
    }
    .dn-toast-root:hover .dn-toast-progress {
      animation-play-state: paused !important;
    }
    @keyframes dn-toast-exit {
      from { opacity: 1; transform: translateX(0) scale(1); }
      to   { opacity: 0; transform: translateX(32px) scale(0.96); }
    }
    .dn-toast-exiting {
      animation: dn-toast-exit 220ms cubic-bezier(0.4, 0, 1, 1) forwards;
    }
  `;
  document.head.appendChild(el);
};

injectToastStyles();

const SlideLeft = React.forwardRef((props, ref) => (
  <Slide {...props} direction="left" ref={ref} />
));

const DnToast = ({ open, onClose, severity, title, message, duration }) => {
  const theme = useTheme();
  const severityKey = SEVERITY_ICONS[severity] ? severity : 'info';
  const Icon = SEVERITY_ICONS[severityKey];
  const accent = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  }[severityKey];
  const progressKey = useRef(0);
  const boxRef = useRef(null);

  useEffect(() => {
    if (open) progressKey.current += 1;
  }, [open]);

  const renderMessage = () => {
    if (React.isValidElement(message)) return message;
    if (typeof message === 'string') {
      return (
        <>
          {title && <strong style={{ display: 'block', marginBottom: 2 }}>{t(title)}</strong>}
          {t(message)}
        </>
      );
    }
    if (message && typeof message === 'object') {
      return (
        <>
          {(title || message.title) && (
            <strong style={{ display: 'block', marginBottom: 2 }}>
              {t(title || message.title)}
            </strong>
          )}
          {message.message && <span>{t(message.message)}</span>}
          {message.details?.map((d, i) => (
            <span key={i} style={{ display: 'block', marginTop: 2, opacity: 0.85 }}>{t(d)}</span>
          ))}
        </>
      );
    }
    return null;
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={duration}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={SlideLeft}
      TransitionProps={{
        timeout: { enter: 320, exit: 220 },
        onExit: () => boxRef.current?.classList.add('dn-toast-exiting'),
        onExited: () => boxRef.current?.classList.remove('dn-toast-exiting'),
      }}
      sx={{ top: '70px !important', right: '24px !important', zIndex: 2000 }}
    >
      <Box
        ref={boxRef}
        className="dn-toast-root"
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderLeft: `3px solid ${accent}`,
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
          px: '14px',
          pt: '12px',
          pb: '16px',
          minWidth: '280px',
          maxWidth: '420px',
          overflow: 'hidden',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {/* Icon */}
        <Icon sx={{ fontSize: 18, color: accent, flexShrink: 0, mt: '1px' }} />

        {/* Content */}
        <Box sx={{ flex: 1, fontSize: 13, lineHeight: 1.5, color: 'text.primary', pr: '4px' }}>
          {renderMessage()}
        </Box>

        {/* Close button */}
        <Box
          component="button"
          onClick={onClose}
          sx={{
            border: 0, background: 'transparent', cursor: 'pointer', padding: '2px',
            display: 'flex', alignItems: 'center', color: 'text.disabled', flexShrink: 0, mt: '-1px',
            borderRadius: '4px',
            '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
          }}
        >
          <CloseOutlined sx={{ fontSize: 14 }} />
        </Box>

        {/* Progress bar */}
        {duration > 0 && (
          <Box
            key={progressKey.current}
            className="dn-toast-progress"
            sx={{
              position: 'absolute',
              bottom: 0, left: 0,
              height: '3px',
              width: '100%',
              background: accent,
              opacity: 0.35,
              animationDuration: `${duration}ms`,
            }}
          />
        )}
      </Box>
    </Snackbar>
  );
};

const AlertMessage = ({
  alertMessage, openAlert, alertSeverity, closeAlert, alertTitle, snackbarProps,
}) => (
  <DnToast
    open={openAlert}
    onClose={closeAlert}
    severity={alertSeverity || 'info'}
    title={alertTitle}
    message={alertMessage}
    duration={snackbarProps?.autoHideDuration ?? 3000}
  />
);

AlertMessage.propTypes = {
  openAlert: PropTypes.bool.isRequired,
  closeAlert: PropTypes.func.isRequired,
  alertMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.node]).isRequired,
  alertSeverity: PropTypes.string.isRequired,
  alertTitle: PropTypes.string,
  snackbarProps: PropTypes.object,
};

export default AlertMessage;
